import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';
import config from '../config';

function AgentRuleBasedCallDialog({ open, onClose, agentCustomId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [ruleDefinitions, setRuleDefinitions] = useState([]);
  const [formData, setFormData] = useState({}); // { ruleName: [string1, string2, ...] } - array of strings
  const [inputValues, setInputValues] = useState({}); // { ruleName: 'current input text' }

  useEffect(() => {
    if (open && agentCustomId) {
      loadRuleDefinitions();
    }
  }, [open, agentCustomId]);

  const loadRuleDefinitions = async () => {
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/rule-definitions`, {
        params: { isActive: 'true' }
      });
      const rules = response.data || [];
      setRuleDefinitions(rules);
      
      // Initialize input values for each rule
      const initialInputs = {};
      rules.forEach(rule => {
        initialInputs[rule.name] = '';
      });
      setInputValues(initialInputs);

      // After loading rule definitions, fetch existing agent data
      if (agentCustomId) {
        await fetchRuleBasedCallData(rules);
      } else {
        // Initialize formData with empty arrays for all rules
        const initialFormData = {};
        rules.forEach(rule => {
          initialFormData[rule.name] = [];
        });
        setFormData(initialFormData);
      }
    } catch (error) {
      console.error('Error loading rule definitions:', error);
    }
  };

  const fetchRuleBasedCallData = async (ruleDefs = null) => {
    try {
      setLoading(true);
      setError('');
      const rulesToUse = ruleDefs || ruleDefinitions;
      
      if (rulesToUse.length === 0) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${config.apiBaseUrl}/api/agent-rule-based-calls/${agentCustomId}`);
      const rules = response.data.rules || {};
      
      // Initialize formData with all rule definitions, including those with no data
      const arrayFormData = {};
      rulesToUse.forEach(ruleDef => {
        const ruleName = ruleDef.name;
        if (rules[ruleName] && Array.isArray(rules[ruleName])) {
          arrayFormData[ruleName] = rules[ruleName];
        } else {
          arrayFormData[ruleName] = []; // Initialize with empty array
        }
      });
      
      setFormData(arrayFormData);
    } catch (error) {
      console.error('Error fetching rule-based call data:', error);
      setError('Failed to load rule-based call configuration');
      
      // If error, still initialize with empty arrays for all rules
      const rulesToUse = ruleDefs || ruleDefinitions;
      if (rulesToUse.length > 0) {
        const arrayFormData = {};
        rulesToUse.forEach(ruleDef => {
          arrayFormData[ruleDef.name] = [];
        });
        setFormData(arrayFormData);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (ruleName, value) => {
    setInputValues(prev => ({
      ...prev,
      [ruleName]: value
    }));
  };

  const handleAddString = (ruleName) => {
    const inputValue = inputValues[ruleName] || '';
    const trimmedValue = inputValue.trim();
    
    if (trimmedValue) {
      setFormData(prev => ({
        ...prev,
        [ruleName]: prev[ruleName] ? [...prev[ruleName], trimmedValue] : [trimmedValue]
      }));
      // Clear the input
      setInputValues(prev => ({
        ...prev,
        [ruleName]: ''
      }));
    }
  };

  const handleRemoveString = (ruleName, index) => {
    setFormData(prev => ({
      ...prev,
      [ruleName]: prev[ruleName] ? prev[ruleName].filter((_, i) => i !== index) : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Include all rules, even with empty arrays
      // Filter out empty strings but keep empty arrays
      const cleanedData = {};
      
      // Ensure all rule definitions are included
      ruleDefinitions.forEach(ruleDef => {
        const ruleName = ruleDef.name;
        const items = formData[ruleName] || [];
        // Filter out empty strings but keep the array (even if empty)
        const filtered = items.filter(item => item && item.trim().length > 0);
        cleanedData[ruleName] = filtered; // Can be empty array
      });

      await axios.post(`${config.apiBaseUrl}/api/agent-rule-based-calls/${agentCustomId}`, {
        rules: cleanedData
      });
      setSuccess('Rule-based call configuration saved successfully!');
      setTimeout(() => {
        setSuccess('');
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error saving rule-based call data:', error);
      setError('Failed to save rule-based call configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseDialog = () => {
    setError('');
    setSuccess('');
    setFormData({});
    setInputValues({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div">
          Rule Based Definition
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {ruleDefinitions.length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                No rules defined yet. Please create rules in Settings first.
              </Alert>
            )}

            <Grid container spacing={3}>
              {ruleDefinitions.map((ruleDef) => {
                const ruleName = ruleDef.name;
                const inputValue = inputValues[ruleName] || '';
                const addedStrings = formData[ruleName] || [];

                return (
                  <Grid item xs={12} key={ruleDef._id}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                      {ruleName}
                    </Typography>
                    
                    {/* Input text box with Add button */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                      <TextField
                        fullWidth
                        size="small"
                        value={inputValue}
                        onChange={(e) => handleInputChange(ruleName, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddString(ruleName);
                          }
                        }}
                        placeholder="Enter a string"
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddString(ruleName)}
                        disabled={!inputValue.trim()}
                      >
                        Add
                      </Button>
                    </Box>

                    {/* Display added strings as chips */}
                    {addedStrings.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {addedStrings.map((str, index) => (
                          <Chip
                            key={index}
                            label={str}
                            onDelete={() => handleRemoveString(ruleName, index)}
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          disabled={saving || loading}
        >
          {saving ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AgentRuleBasedCallDialog;

